import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttorney } from '../../context/AttorneyContext';
import { useInternalRequests, useContactClientForRequest, useDeclineInternalRequest } from '../../hooks/useMessaging';
import ContactClientModal from './ContactClientModal';
import './InternalRequests.css';

/**
 * Internal Requests Queue
 * Shows pending requests from external attorneys that need MarkoTM action.
 * Only visible to MarkoTM attorneyadmins.
 */
function InternalRequests() {
  const { t } = useTranslation('attorney');
  const { canProcessInternalRequests, firmName } = useAttorney();
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);

  const { data: requestsData, isLoading } = useInternalRequests(activeTab);
  const requests = requestsData?.requests || [];

  // Access check - only MarkoTM attorneyadmins can see this
  if (!canProcessInternalRequests) {
    return (
      <div className="internal-requests-page">
        <div className="access-denied">
          <h2>Access Restricted</h2>
          <p>
            Only MarkoTM administrators can process internal requests.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="internal-requests-page">
      <div className="page-header">
        <h1>{t('attorney.internalRequests.title', 'Internal Requests')}</h1>
        <p className="page-subtitle">
          {t('attorney.internalRequests.subtitle', 'Requests from external attorneys that need MarkoTM action')}
        </p>
      </div>

      <div className="requests-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          {t('attorney.internalRequests.pending', 'Pending')}
          {requests.length > 0 && activeTab === 'pending' && (
            <span className="tab-badge">{requests.length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'processed' ? 'active' : ''}`}
          onClick={() => setActiveTab('processed')}
        >
          {t('attorney.internalRequests.processed', 'Processed')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'declined' ? 'active' : ''}`}
          onClick={() => setActiveTab('declined')}
        >
          {t('attorney.internalRequests.declined', 'Declined')}
        </button>
      </div>

      <div className="requests-list">
        {isLoading ? (
          <div className="loading-state">Loading requests...</div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            {activeTab === 'pending'
              ? t('attorney.internalRequests.noPending', 'No pending requests from external attorneys')
              : activeTab === 'processed'
              ? t('attorney.internalRequests.noProcessed', 'No processed requests')
              : t('attorney.internalRequests.noDeclined', 'No declined requests')}
          </div>
        ) : (
          requests.map((request) => (
            <InternalRequestCard
              key={request.id}
              request={request}
              onContactClient={() => setSelectedRequest(request)}
              showActions={activeTab === 'pending'}
            />
          ))
        )}
      </div>

      {selectedRequest && (
        <ContactClientModal
          isOpen={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
          request={selectedRequest}
        />
      )}
    </div>
  );
}

/**
 * Internal Request Card Component
 */
function InternalRequestCard({ request, onContactClient, showActions }) {
  const { t } = useTranslation('attorney');
  const [expanded, setExpanded] = useState(false);
  const declineRequest = useDeclineInternalRequest();
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);

  const urgencyColors = {
    low: 'urgency-low',
    medium: 'urgency-medium',
    high: 'urgency-high',
    urgent: 'urgency-urgent'
  };

  const statusIcons = {
    pending: '📋',
    client_contacted: '📧',
    response_forwarded: '✅',
    declined: '❌'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining');
      return;
    }

    try {
      await declineRequest.mutateAsync({
        requestId: request.id,
        reason: declineReason
      });
      setShowDeclineForm(false);
      setDeclineReason('');
    } catch (error) {
      alert(`Error declining request: ${error.message}`);
    }
  };

  return (
    <div className={`request-card ${expanded ? 'expanded' : ''}`}>
      <div className="request-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="request-info">
          <span className="request-status-icon">{statusIcons[request.status] || '📋'}</span>
          <div className="request-meta">
            <span className="request-title">{request.title}</span>
            <span className="request-from">
              From: <strong>{request.metadata?.requestingAttorneyName || 'External Attorney'}</strong>
              {request.metadata?.requestingFirmName && ` (${request.metadata.requestingFirmName})`}
            </span>
            <span className="request-application">
              {t('attorney.internalRequests.re', 'Re')}: {request.applicationId || 'Application'}
              {request.metadata?.clientName && ` - ${request.metadata.clientName}`}
            </span>
          </div>
        </div>
        <div className="request-right">
          <span className={`urgency-badge ${urgencyColors[request.urgency] || ''}`}>
            {request.urgency}
          </span>
          <span className="request-date">{formatDate(request.createdAt)}</span>
        </div>
      </div>

      {expanded && (
        <div className="request-card-body">
          <div className="request-content">
            <h4>{t('attorney.internalRequests.requestDetails', 'Request Details')}</h4>
            <p>{request.message}</p>
          </div>

          {request.requestData?.items && request.requestData.items.length > 0 && (
            <div className="request-items">
              <h4>{t('attorney.internalRequests.requestedItems', 'Information Requested')}</h4>
              <ul>
                {request.requestData.items.map((item, idx) => (
                  <li key={idx}>
                    <span className="item-type">[{item.type}]</span>
                    <span className="item-label">{item.label}</span>
                    {item.required && <span className="item-required">*</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {request.status === 'declined' && request.declineReason && (
            <div className="decline-reason">
              <h4>{t('attorney.internalRequests.declineReason', 'Decline Reason')}</h4>
              <p>{request.declineReason}</p>
            </div>
          )}

          {showActions && (
            <div className="request-actions">
              {!showDeclineForm ? (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactClient();
                    }}
                  >
                    {t('attorney.internalRequests.contactClient', 'Contact Client')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeclineForm(true);
                    }}
                  >
                    {t('attorney.internalRequests.decline', 'Decline')}
                  </button>
                </>
              ) : (
                <div className="decline-form" onClick={(e) => e.stopPropagation()}>
                  <textarea
                    placeholder="Reason for declining this request..."
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    rows={3}
                  />
                  <div className="decline-form-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowDeclineForm(false);
                        setDeclineReason('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={handleDecline}
                      disabled={declineRequest.isPending}
                    >
                      {declineRequest.isPending ? 'Declining...' : 'Confirm Decline'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {request.status === 'response_forwarded' && request.forwardedAt && (
            <div className="forwarded-info">
              <span className="success-badge">
                {t('attorney.internalRequests.forwarded', 'Response forwarded on')} {formatDate(request.forwardedAt)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default InternalRequests;
